import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FirebaseAuthContext } from 'context/FirebaseAuthContext.js';
import { db } from 'config/firebase.js'
import { query, collection, addDoc, updateDoc, limit, where, getDocs, getDoc, doc, orderBy, getCountFromServer, setDoc, deleteDoc, arrayUnion } from 'firebase/firestore';
import { useCollectionData, useDocumentData, useCollection } from 'react-firebase-hooks/firestore';
import PageHeader from 'components/common/PageHeader';
import { Card, Row, Col, Button, Form, Tabs, Tab, Image, Modal, Alert, Spinner } from 'react-bootstrap';
import AdvanceTableWrapper from 'components/merlin/advance-table/AdvanceTableWrapper';
import AdvanceTableSearchBox from 'components/merlin/advance-table/AdvanceTableSearchBox';
import AdvanceTable from 'components/merlin/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/merlin/advance-table/AdvanceTableFooter';
import Lottie from 'lottie-react';
import dotsAnimation from 'assets/lottie/dots.json';
import { toast } from 'react-toastify';

const BatchToIBMi = () => {

    const [batchesRaw, batchNamesLoading, batchReadError] = useCollection(
        query(
            collection(db, "batchState"),
            orderBy('sortableTimestamp', 'desc')
        ), {
            snapshotListenOptions: { includeMetadataChanges: true },
    });

    // Process batches to include document refs (needed for View/Edit functionality)
    const batches = batchesRaw?.docs?.map((doc) => ({
        ...doc.data(),
        ref: doc.ref,
        docId: doc.id
    })) || [];
    const [orders, ordersLoading, ordersReadError] = useCollectionData(
        query(
            collection(db, "openOrders"),
            orderBy('lastUpdate', 'desc')
        ), {
            snapshotListenOptions: { includeMetadataChanges: true },
    });
    const [errorsRaw, errorsLoading, errorsReadError] = useCollection(
        query(
            collection(db, "batchToIBMiErrors"),
            orderBy('createTimestamp', 'desc')
        )
    );
    const [errors, setErrors] = useState([]);
    const [accountingExclusions, accountingExclusionsLoading, accountingExclusionsReadError] = useCollectionData(
        query(
            collection(db, "orders"),
            where('accountingExclusion', '==', true),
            orderBy('modifyTimestamp', 'desc')
        ), {
            snapshotListenOptions: { includeMetadataChanges: true },
    });
    const [locations, locationsLoading, locationsReadError] = useCollectionData(
        query(
            collection(db, "locations"),
            orderBy('name', 'asc')
        ), {
            snapshotListenOptions: { includeMetadataChanges: true },
    });

    const [batchMetadata, metaLoading, metaError] = useDocumentData(doc(db, 'shipStationMeta', 'fulfillments'));
    const { user, authLoading }= useContext(FirebaseAuthContext)
    const navigate = useNavigate();

    const [openCarts, setOpenCarts] = useState();
    
    useEffect(() => {
      if (authLoading) return;
      if (!user) return navigate('/');

      async function getOpenCarts(uid) {
        try {
          const openOrders = []
          const q = query(
            query(
              collection(db, "openOrders"),
              where("uid", "==", uid), 
              where("status", "==", "Open")
            )
          )
          const querySnapshot = await getDocs(q);
          querySnapshot.forEach((doc) => {
            openOrders.push({docId: doc.id, ...doc.data()})
          })
          
          return openOrders
        } catch (error) {
          console.log("getOpenOrders", error);
        }
      }
  
      getOpenCarts(user.uid).then((carts) => {
        setOpenCarts(carts)
      });

    }, [authLoading, user])

    // Process errors to include document refs
    useEffect(() => {
        if (errorsLoading || !errorsRaw) return;
        const processedErrors = errorsRaw.docs.map((doc) => ({
            ...doc.data(),
            ref: doc.ref,
            docId: doc.id
        }));
        setErrors(processedErrors);
    }, [errorsRaw, errorsLoading]);

    // Fix modal state
    const [showFixModal, setShowFixModal] = useState(false);
    const [modalStep, setModalStep] = useState(1);
    const [selectedError, setSelectedError] = useState(null);
    const [parentProduct, setParentProduct] = useState(null);
    const [variantLoading, setVariantLoading] = useState(null);
    const [variantName, setVariantName] = useState('');
    const [productFactor, setProductFactor] = useState('1');
    const [selectedCart, setSelectedCart] = useState('');
    const [newCartName, setNewCartName] = useState('');
    const [saving, setSaving] = useState(false);

    // Lookup parent product by first 6 digits of SKU
    async function lookupParentProduct(variantSku) {
        const parentSku = variantSku.split("-")[0];
        if (parentSku.length !== 6) return null;

        const q = query(
            collection(db, "productCatalog/topicz/products"),
            where("sku", "==", parentSku)
        );
        const snap = await getDocs(q);
        if (snap.empty) return null;
        return { ...snap.docs[0].data(), ref: snap.docs[0].ref };
    }

    // Handle Fix button click - checks for bundle or parent product
    async function handleFixClick(e, errorRecord) {
        e.stopPropagation();
        const sku = errorRecord.orderData.sku;
        setVariantLoading(sku);

        // 1. First check if it's a bundle
        const bundleQuery = query(
            collection(db, "productBundles"),
            where("sku", "==", sku)
        );
        const bundleSnap = await getDocs(bundleQuery);

        if (!bundleSnap.empty) {
            setVariantLoading(null);
            toast.info('This is a bundle. Redirecting to bundle configuration...');
            navigate(`/bundles?edit=${sku}`);
            return;
        }

        // 2. Not a bundle - try to find parent product
        const parent = await lookupParentProduct(sku);

        if (!parent) {
            setVariantLoading(null);
            toast.error('Cannot find parent product or bundle for this SKU');
            return;
        }

        // 3. Parent found - open variant creation modal
        setParentProduct(parent);
        setSelectedError(errorRecord);
        setVariantName(errorRecord.orderData.title || '');
        setProductFactor('1');
        setModalStep(1);
        setSelectedCart('');
        setNewCartName('');
        setShowFixModal(true);
        setVariantLoading(null);
    }

    // Create variant and enable Seller Active sync
    async function handleCreateVariant(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const variantSku = selectedError.orderData.sku;

            // Validate productFactor: must be whole number >= 1
            const parsedPF = parseInt(productFactor, 10);
            if (isNaN(parsedPF) || parsedPF < 1 || !Number.isInteger(parsedPF)) {
                toast.error('Product Factor must be a whole number of 1 or greater', { theme: 'colored' });
                return;
            }

            const variantData = {
                parentRef: parentProduct.ref,
                parentSku: parentProduct.sku,
                sku: variantSku,
                title: variantName,
                productFactor: parsedPF,
                modifyDate: new Date(),
                sellerActiveStatus: true
            };

            // 1. Create variant in product subcollection
            const variantRef = doc(
                collection(parentProduct.ref, 'variants'),
                variantSku
            );
            await setDoc(variantRef, variantData);

            // 2. Enable Seller Active sync
            const sellerActiveRef = doc(db, 'productsMeta', 'sellerActive', 'active', variantSku);
            await setDoc(sellerActiveRef, {
                sku: variantSku,
                refPath: parentProduct.ref,
                active: true
            }, { merge: true });

            toast.success(`Variant ${variantSku} created and synced to Seller Active`);
            setModalStep(2);
        } catch (error) {
            console.error('Error creating variant:', error);
            toast.error('Failed to create variant');
        }
        setSaving(false);
    }

    // Add item to existing cart
    async function handleAddToCart() {
        if (!selectedCart) {
            toast.error('Please select a cart');
            return;
        }
        setSaving(true);
        try {
            const docRef = doc(db, "openOrders", selectedCart);
            await updateDoc(docRef, {
                items: arrayUnion({
                    picture: selectedError.orderData.picture || '',
                    quantity: selectedError.orderData.quantity || 1,
                    sku: selectedError.orderData.sku,
                    title: variantName,
                    cost: "0.00",
                }),
            });

            // Delete the error record
            await deleteDoc(selectedError.ref);

            toast.success('Item added to cart');
            setShowFixModal(false);
        } catch (error) {
            console.error('Error adding to cart:', error);
            toast.error('Failed to add to cart');
        }
        setSaving(false);
    }

    // Create new cart and add item
    async function handleCreateNewCart() {
        if (!newCartName.trim()) {
            toast.error('Please enter a cart name');
            return;
        }
        setSaving(true);
        try {
            // 1. Create new cart atomically (generate ID first to include docId in initial write)
            const cartRef = doc(collection(db, "openOrders"));
            await setDoc(cartRef, {
                docId: cartRef.id,
                orderBatchName: newCartName.trim(),
                uid: user.uid,
                status: "Open"
            });

            // 2. Add the item
            await updateDoc(doc(db, "openOrders", cartRef.id), {
                items: arrayUnion({
                    picture: selectedError.orderData.picture || '',
                    quantity: selectedError.orderData.quantity || 1,
                    sku: selectedError.orderData.sku,
                    title: variantName,
                    cost: "0.00",
                }),
            });

            // 3. Delete the error record
            await deleteDoc(selectedError.ref);

            toast.success('Cart created and item added');
            setShowFixModal(false);
            navigate(`/cart/${cartRef.id}`);
        } catch (error) {
            console.error('Error creating cart:', error);
            toast.error('Failed to create cart');
        }
        setSaving(false);
    }

    // Handle "Add to Order" button - decides between existing cart or new cart
    function handleAddToOrder() {
        if (newCartName.trim()) {
            handleCreateNewCart();
        } else if (selectedCart) {
            handleAddToCart();
        } else {
            toast.error('Please select a cart or enter a new cart name');
        }
    }

    async function getOrderCountForBatch(batchName) {
        try {
            const q = query(collection(db, "fulfillments"), where("batchNumber", "==", batchName));
            const snapshot = await getDocs(q);
            // Deduplicate by orderNumber - an order can have multiple shipments
            const uniqueOrderNumbers = new Set();
            snapshot.forEach((doc) => {
                uniqueOrderNumbers.add(doc.data().orderNumber);
            });
            return uniqueOrderNumbers.size;
        } catch (error) {
            console.log(error)
            return 'Error';
        }
    }
    
    const batchColumns = [
        {
          accessor: 'batchName',
          Header: 'Name'
        },
        {
            accessor: 'sortableTimestamp',
            Header: 'Timestamp',
            Cell: rowData => {
            const { timestamp } = rowData.row.original;
            const timestring = timestamp.toDate().toLocaleString();
            return (
                <span>
                    {timestring}
                </span>
            );

            },
            //sortType: "datetime", // only used with Date object
        },
        {
            accessor: 'timestamp',
            Header: 'Do Not Pick?',
            Cell: rowData => {
                const { ref, doNotPick, state } = rowData.row.original;
                let disabled;
                if (state === 'SENT') disabled = true;
                return (
                    <span>
                        {ref ?
                          <Form.Check 
                            type='switch'
                            id='defaultSwitch'
                            label=''
                            onChange={() => { updatePickStatus(ref, !doNotPick)}}
                            defaultChecked={doNotPick}
                            disabled={disabled}
                          />
                        :
                          'N/A'
                        }
                    </span>
                );

            },
            //sortType: "datetime", // only used with Date object
        },
        {
            //accessor: 'state',
            Header: 'Fulfillment Location',
            Cell: rowData => {
                // get Inventory locations from firebase
                
                return (
                    <>
                        <Form.Select
                            aria-label="Default select example"
                            onChange={(e) => { updateFulfillmentLocation(rowData.row.original.ref, e.target.value)}}
                            defaultValue={rowData.row.original.fulfillmentLocation}
                            disabled={rowData.row.original.state === 'SENT'}
                        >
                            <option key={'selOpt'} value=''>Select Location</option>
                            {locations && locations.map((location, i) => {
                                return (
                                    <option key={`${i}-opt`} value={location.name}>{location.name}</option>
                                )
                            })}
                            <option key={'nonOpt'} value={'None'}>None</option>
                        </Form.Select>
                    </>
                )
            }
        },
        {
            Header: 'Order Count',
            Cell: rowData => {
                const { batchName } = rowData.row.original;
                const [orderCount, setOrderCount] = useState(null);
    
                useEffect(() => {
                    getOrderCountForBatch(batchName).then(setOrderCount);
                }, [batchName]);
    
                return <span>{orderCount !== null ? orderCount : 'Loading...'}</span>;
            },
        },
        {
            accessor: 'state',
            Header: 'State',
            Cell: rowData => {
                const batch = rowData.row.original;
                const state = batch.state;
                const hash = String(batchHashCode(batch.batchName));
                const reSubmitHash = String(batchHashCode(batch.batchName + 'resubmit'));
                let disabled;
                if (state === 'SENT') disabled = true;

                return (
                    <> 
                        <Button
                            variant='falcon-primary'
                            className='mt-2'
                            onClick={(e) => handleBatchSubmit(e, batch)}
                            id={hash}
                            disabled={disabled}
                            style={{width: '5rem'}}
                            size="sm"
                        >
                            {!disabled ? 'Send' : 'Sent'}
                        </Button>
                        <Button
                            variant='falcon-success'
                            className='ms-2 mt-2'
                            onClick={() => navigate(`/batches/${batch.ref.id}`)}
                            size="sm"
                        >
                            View
                        </Button>
                        <Button
                            variant='falcon-warning'
                            className='ms-2 mt-2'
                            onClick={(e) => handleResubmit(e, batch)}
                            id={reSubmitHash}
                            disabled={!disabled}
                            size="sm"
                        >
                            Resubmit
                        </Button>
                    </>
                )
            }
        }
      ];

    const orderColumns = [
        {
          accessor: 'orderBatchName',
          Header: 'Name'
        },
        {
            accessor: 'lastUpdate',
            Header: 'Timestamp',
            Cell: rowData => {
            const { lastUpdate } = rowData.row.original;
            const timestring = lastUpdate.toDate().toLocaleString();
            return (
                <span>
                    {timestring}
                </span>
            );

            },
            //sortType: "datetime", // only used with Date object
        },
        {
            accessor: 'state',
            Header: 'State',
            Cell: rowData => {
                const order = rowData.row.original;
                const status = order.status;
                const hash = String(batchHashCode(order.orderBatchName));
                let disabled;
                if (status === 'Processed') disabled = true;

                return (
                    <> 
                        <Button
                            variant='falcon-primary'
                            className='mt-2'
                            onClick={(e) => {console.log('click')}}
                            id={hash}
                            disabled={true}
                            style={{width: '7rem'}}
                            size="sm"
                        >
                            {!disabled ? 'Send' : 'Processed'}
                        </Button>
                    </>
                )
            }
        }
      ];

    const errorColumns = [
        {
            accessor: 'orderData',
            Header: 'Items',
            Cell: rowData => {
                const { orderData } = rowData.row.original;
                return (
                    <div className="d-flex align-items-center">
                        <Image src={orderData?.picture} width={'40px'} className='me-2' style={{objectFit: 'contain'}}/>
                        <span className="text-truncate" style={{maxWidth: '200px'}}>{orderData?.title}</span>
                    </div>
                );
            },
        },
        {
            accessor: 'variantSku',
            Header: 'Variant SKU',
            Cell: rowData => {
                const { orderData } = rowData.row.original;
                return <span className="fw-bold">{orderData?.sku}</span>;
            },
        },
        {
            accessor: 'parentSku',
            Header: 'Parent',
            Cell: rowData => {
                const { orderData } = rowData.row.original;
                const sku = orderData?.sku || '';
                const parentSku = sku.split("-")[0];
                const isValidParent = parentSku.length === 6;
                return (
                    <span className={isValidParent ? 'text-success' : 'text-warning'}>
                        {parentSku} {isValidParent ? '✓' : ''}
                    </span>
                );
            },
        },
        {
            accessor: 'batch',
            Header: 'Batch'
        },
        {
            accessor: 'createTimestamp',
            Header: 'Timestamp',
            Cell: rowData => {
                const { createTimestamp } = rowData.row.original;
                const date = new Date(createTimestamp);
                const timestring = date.toLocaleDateString();
                return <span>{timestring}</span>;
            },
        },
        {
            accessor: 'action',
            Header: 'Action',
            Cell: rowData => {
                const errorRecord = rowData.row.original;
                const sku = errorRecord.orderData?.sku;
                const isLoading = variantLoading === sku;

                return (
                    <Button
                        variant="falcon-primary"
                        size="sm"
                        onClick={(e) => handleFixClick(e, errorRecord)}
                        disabled={isLoading}
                    >
                        {isLoading ? <Spinner size="sm" /> : 'Fix →'}
                    </Button>
                );
            }
        }
    ];

    async function updateFulfillmentLocation(reference, value) {
        if (!value || value === 'Select Location') return;
        try {
            await updateDoc(reference, {
                fulfillmentLocation: value
            });
        } catch (error) {
            console.log(error);
        }

    }
      
    const AddToOrderCartForm = (data) => {

        const carts = data?.carts;
        const items = data?.items;
        const key = data?.orderKey;
        const ssOrderId = data?.ssOrderId;

        async function addToCart(e, items, key) {
        e.preventDefault();
        const cartId = e.target.cartName.value;
        
        try {
            const docRef = doc(db, "openOrders", cartId);
            for (let i = 0; i < items.length; i++) {
            await updateDoc(docRef, {
                items: arrayUnion({
                    picture: items[i]?.imageUrl,
                    quantity: items[i]?.quantity,
                    sku: items[i]?.sku,
                    title: items[i]?.name,
                    cost: "0.00",
                }),
                })
                //markOrderAsPrePicked(key)
            }
            
        } catch (error) {
            console.log(error);
        }
        
        }

        return (
        <Form onSubmit={(e) => addToCart(e, items, key)}>
            <Row>
            <Col>
                <Form.Select
                id='cartName'
                >
                {carts ?
                    carts.map((cart, i) => {
                    return (<option key={`${i}-opt`} value={cart.docId}>{cart.orderBatchName}</option>)
                })
                : null
                }
                </Form.Select>
            </Col>
            </Row>
            <Row>
            <Col>
                <Button className='mt-2' variant='falcon-primary' type="submit">Add</Button>
            </Col>
            </Row>
            
        </Form>
        )
    }


    const accountingExclusionColumns = [
        {
            accessor: 'orderNumber',
            Header: 'Order Number',
            Cell: rowData => {
                const { orderNumber, orderId } = rowData.row.original;
                return (
                    <span>
                        <a href={`/orders/${orderId}`} target="_blank" rel="noreferrer">
                            {orderNumber}
                        </a>
                    </span>
                );
            }
        },
        {
            accessor: 'items',
            Header: 'Items',
            Cell: rowData => {
                const { items } = rowData.row.original;

               
                return (
                    <table>
                        <tbody>
                            {items.map((item, index) => {
                                return (
                                    <tr key={index}>
                                        <td>
                                        {item.sku} - {item.name}
                                            <br/>
                                            
                                            
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                );
            }
        },
        {
            accessor: '',
            Header: 'Add to Order',
            Cell: rowData => {
              const { items, orderKey, prePicked, orderId } = rowData.row.original;
              const processedItems = [];
              items.forEach((item) => {
                const firstLetter = item.sku.slice(0,1);
                if (firstLetter === 'B' || firstLetter === 'S' ) {
                  /** TODO Need to manage bundles / kits */
                } else {
                  processedItems.push({
                    sku: item.sku,
                    quantity: item.quantity
                  })
                }
              })
              
              return (
                <span>
                {processedItems.length > 0 && prePicked !== true ?
                  <AddToOrderCartForm carts={openCarts} items={items} orderKey={orderKey} orderId={orderId} />
                : prePicked ? <span className='fs-1 text-primary'>Pre-Picked</span> : null
                }
                </span>
              );
            }
          },
    ];

    async function getProductDetails(sku) {
        try {
            const q = query(collection(db, "productCatalog/topicz/products"), where("sku", "==", sku));
            const querySnapshot = await getDocs(q);
            if (querySnapshot.empty) return;
            const product = querySnapshot.docs[0].data();
            return product;
        } catch (error) {
            console.log(error)
        }
    }

    async function updatePickStatus(docRef, state) {
        try {
            await updateDoc(docRef, {
                doNotPick: state
            })
        } catch (error) {
            console.log(error)
        }
    }

    function batchHashCode(batchName) {
        let h;
        for ( let i = 0; i < batchName.length; i++ ) {
            h = Math.imul(31, h) + batchName.charCodeAt(i) | 0;
        } 

      return Math.abs(h);
    }

    async function handleBatchSubmit(e, batch) {
        e.preventDefault();
        const hash = batchHashCode(batch.batchName);
        const button = document.getElementById(String(hash));
        button.disabled = true;
        button.innerText = "Sending...";

        // Fetch fresh batch data from Firestore to avoid stale closure bug
        // This ensures we get the current doNotPick value even if the user
        // toggled the switch after the page loaded
        const freshDoc = await getDoc(batch.ref);
        const freshBatch = { ...freshDoc.data(), ref: batch.ref };

        const newOrderRef = await addOrder(freshBatch);
        await updateBatchStatus(batch.batchName, 'SENT', newOrderRef);
        button.innerText = "Sent"
        return 'SENT';
     }
    
    async function updateBatchStatus(name, status, orderRef) {
        try {
            const q = query(collection(db, "batchState"), where("batchName", "==", name), limit(1))
            const snap = await getDocs(q);
            if (!snap.empty) {

                const res = await updateDoc(snap.docs[0].ref, {
                    state: String(status),
                    orderRef: orderRef
                })
                return res;
            } else {
                console.log("no results")
            }
            
        } catch (error) {
            console.log(error)
        }
        
    }

    async function addOrder(batch) {
        try {
            const doNotPick = batch?.doNotPick || false;
            const orderRef = await addDoc( collection(db, "batchToIBMiOrders"), {
                timestamp: new Date(),
                orderBatchName: batch.batchName,
                status: 'Submitted',
                uid: user.uid,
                doNotPick: doNotPick,
            });
            return orderRef;
        } catch (error) {
            console.log(error)
        }
        
    }

    async function handleResubmit(e, batch) {
        e.preventDefault();
        const hash = batchHashCode(batch.batchName);
        const resubmitHash = batchHashCode(batch.batchName + 'resubmit');
        const submitButton = document.getElementById(String(hash));
        const reSubmitButton = document.getElementById(String(resubmitHash));
        submitButton.disabled = true;
        submitButton.innerText = "Resending...";
        reSubmitButton.disabled = true;
        reSubmitButton.innerText = "Resending...";
        const res = await resubmitOrder(batch);
        submitButton.innerText = "Sent"
        return res;
    }

    async function resubmitOrder(batch) {
        try {
            // Fetch fresh batch data from Firestore to avoid stale closure bug
            const freshDoc = await getDoc(batch.ref);
            const doNotPick = freshDoc.data()?.doNotPick || false;

            // query the batchToIBMiOrders collection for the order
            const q = query(collection(db, "batchToIBMiOrders"), where("orderBatchName", "==", batch.batchName), limit(1));
            const snap = await getDocs(q);
            if (!snap.empty) {
                const res = await updateDoc(snap.docs[0].ref, {
                    status: 'Submitted',
                    doNotPick: doNotPick
                })
                return res;
            } else {
                console.log("no results")
                // alert the user that the order was not found
                // create a toast
            }
        } catch (error) {
            console.log(error)
        }
    }

    const lastSyncDisplay = batchMetadata?.lastSync ? batchMetadata.lastSync : 'now...';
    
    return (
        <>
            <PageHeader
                title="Order & Batch Processing" titleTag="h5" className="mb-3">
            {!metaLoading ? <span>Current as of {lastSyncDisplay}</span> : 'Loading...'}
            </PageHeader>
            {!batchNamesLoading && !ordersLoading && !errorsLoading ? 
                <Tabs variant='pills' defaultActiveKey="shipStationKey" id="" className='mb-3'>
                <Tab eventKey="shipStationKey" title="ShipStation Batches" className=''>
                    <Card className="p-3 mb-3">
                        {!batchNamesLoading ? 
                            <AdvanceTableWrapper
                            columns={batchColumns}
                            data={batches}
                            sortable
                            pagination
                            perPage={10}
                            >
                                <Row className="flex-end-center mb-3">
                                    <Col>
                                        <AdvanceTableSearchBox table/>
                                    </Col>
                                </Row>
                                <AdvanceTable
                                table
                                headerClassName="bg-200 text-900 text-nowrap align-middle"
                                rowClassName="align-middle white-space-nowrap"
                                tableProps={{
                                    bordered: true,
                                    striped: true,
                                    className: 'fs--1 mb-0 overflow-hidden'
                                }}
                                />
                                <div className="mt-3">
                                <AdvanceTableFooter
                                    rowCount={batches?.length}
                                    table
                                    rowInfo
                                    navButtons
                                    rowsPerPageSelection
                                />
                                </div>
                        </AdvanceTableWrapper>

                            : 'Loading...'
                        }
                    </Card>
                </Tab>
                <Tab eventKey="manualOrderKey" title="Manual Orders" className=''>
                    <Card className="p-3 mb-3">
                        {!ordersLoading ? 
                            <AdvanceTableWrapper
                            columns={orderColumns}
                            data={orders}
                            sortable
                            pagination
                            perPage={10}
                            >
                                <Row className="flex-end-center mb-3">
                                <Col xs="auto" sm={6} lg={4}>
                                    <AdvanceTableSearchBox table/>
                                </Col>
                                </Row>
                                <AdvanceTable
                                table
                                headerClassName="bg-200 text-900 text-nowrap align-middle"
                                rowClassName="align-middle white-space-nowrap"
                                tableProps={{
                                    bordered: true,
                                    striped: true,
                                    className: 'fs--1 mb-0 overflow-hidden'
                                }}
                                />
                                <div className="mt-3">
                                <AdvanceTableFooter
                                    rowCount={batches?.length}
                                    table
                                    rowInfo
                                    navButtons
                                    rowsPerPageSelection
                                />
                                </div>
                        </AdvanceTableWrapper>

                            : 'Loading...'
                        }
                    </Card>
                </Tab>
                <Tab eventKey="errorKey" title="Submission Errors" className=''>
                    <Card className="p-3 mb-3">
                        {!errorsLoading ? 
                            <AdvanceTableWrapper
                            columns={errorColumns}
                            data={errors}
                            sortable
                            pagination
                            perPage={10}
                            >
                                <Row className="flex-end-center mb-3">
                                <Col xs="auto" sm={6} lg={4}>
                                    <AdvanceTableSearchBox table/>
                                </Col>
                                </Row>
                                <AdvanceTable
                                table
                                headerClassName="bg-200 text-900 text-nowrap align-middle"
                                rowClassName="align-middle white-space-nowrap"
                                tableProps={{
                                    bordered: true,
                                    striped: true,
                                    className: 'fs--1 mb-0 overflow-hidden'
                                }}
                                />
                                <div className="mt-3">
                                <AdvanceTableFooter
                                    rowCount={errors?.length}
                                    table
                                    rowInfo
                                    navButtons
                                    rowsPerPageSelection
                                />
                                </div>
                        </AdvanceTableWrapper>

                            : 'Loading...'
                        }
                    </Card>
                </Tab>
                <Tab eventKey="accountingExclusionsKey" title="Accounting Exclusions" className=''>
                    <Card className="p-3 mb-3">
                        {!accountingExclusionsLoading ?
                            <AdvanceTableWrapper
                            columns={accountingExclusionColumns}
                            data={accountingExclusions}
                            sortable
                            pagination
                            perPage={10}
                            >
                                <Row className="flex-end-center mb-3">
                                <Col xs="auto" sm={6} lg={4}>
                                    <AdvanceTableSearchBox table/>
                                </Col>
                                </Row>
                                <AdvanceTable
                                table
                                headerClassName="bg-200 text-900 text-nowrap align-middle"
                                rowClassName="align-middle white-space-nowrap"
                                tableProps={{
                                    bordered: true,
                                    striped: true,
                                    className: 'fs--1 mb-0 overflow-hidden'
                                }}
                                />
                                <div className="mt-3">
                                <AdvanceTableFooter
                                    rowCount={accountingExclusions?.length}
                                    table
                                    rowInfo
                                    navButtons
                                    rowsPerPageSelection
                                />
                                </div>
                        </AdvanceTableWrapper>
                        : 'Loading...'
                    }
                    </Card>
                </Tab>
            </Tabs>
            :
            <div>
                <Row className="align-items-center">
                <Col>
                    <Lottie animationData={dotsAnimation} style={{height: 300}} />
                </Col>
                </Row>
            </div>
            }

            {/* Fix Submission Error Modal */}
            <Modal show={showFixModal} onHide={() => setShowFixModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalStep === 1 ? 'Create Missing Variant' : 'Add to Order'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalStep === 1 ? (
                        <>
                            <Alert variant="info">
                                <strong>Parent Product:</strong> {parentProduct?.sku} - {parentProduct?.name || parentProduct?.shopify_title || 'N/A'}
                                <br />
                                <strong>Cost:</strong> ${parentProduct?.ibmi?.cost || parentProduct?.cost || '0.00'}
                            </Alert>
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>Variant SKU (pre-filled)</Form.Label>
                                    <Form.Control value={selectedError?.orderData?.sku || ''} disabled />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Variant Name</Form.Label>
                                    <Form.Control
                                        value={variantName}
                                        onChange={(e) => setVariantName(e.target.value)}
                                        placeholder="Enter variant name..."
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Product Factor</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        step="1"
                                        value={productFactor}
                                        onChange={(e) => setProductFactor(e.target.value)}
                                        required
                                    />
                                    <Form.Text className="text-muted">
                                        Multiplier for pricing. Default is 1.
                                    </Form.Text>
                                </Form.Group>
                            </Form>
                        </>
                    ) : (
                        <>
                            <Alert variant="success">
                                ✓ Variant <strong>{selectedError?.orderData?.sku}</strong> created successfully!
                            </Alert>
                            <p>Add this item to a cart for resubmission:</p>
                            {openCarts?.length > 0 && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Select Existing Cart</Form.Label>
                                    <Form.Select
                                        value={selectedCart}
                                        onChange={(e) => {
                                            setSelectedCart(e.target.value);
                                            setNewCartName('');
                                        }}
                                    >
                                        <option value="">-- Select Cart --</option>
                                        {openCarts.map(cart => (
                                            <option key={cart.docId} value={cart.docId}>
                                                {cart.orderBatchName}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            )}
                            <Form.Group className="mb-3">
                                <Form.Label>{openCarts?.length > 0 ? 'Or Create New Cart' : 'Create New Cart'}</Form.Label>
                                <Form.Control
                                    placeholder="Enter cart name..."
                                    value={newCartName}
                                    onChange={(e) => {
                                        setNewCartName(e.target.value);
                                        setSelectedCart('');
                                    }}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowFixModal(false)}>
                        Cancel
                    </Button>
                    {modalStep === 1 ? (
                        <Button
                            variant="primary"
                            onClick={handleCreateVariant}
                            disabled={saving || !variantName.trim()}
                        >
                            {saving ? <Spinner size="sm" className="me-2" /> : null}
                            Save & Continue →
                        </Button>
                    ) : (
                        <Button
                            variant="primary"
                            onClick={handleAddToOrder}
                            disabled={saving || (!selectedCart && !newCartName.trim())}
                        >
                            {saving ? <Spinner size="sm" className="me-2" /> : null}
                            Add to Order
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

        </>
    )
}

export default BatchToIBMi;



